import React from "react";
import {
  ResponsiveContainer,
  AreaChart as ReAreaChart,
  Area,
  Tooltip,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
} from "recharts";

type AreaSpec = {
  dataKey: string;
  stroke: string;
  fill?: string;
  fillOpacity?: number;
  strokeWidth?: number;
  dot?: boolean;
  type?: "monotone" | "linear" | "step";
};

interface AreaChartWrapperProps {
  data: Array<Record<string, any>>;
  xKey?: string;
  areas?: AreaSpec[];
  containerClassName?: string;
  hideXAxis?: boolean;
  hideYAxis?: boolean;
  xAxisProps?: Record<string, any>;
  yAxisProps?: Record<string, any>;
  tooltipFormatter?: (value?: number) => string | [string, string];
  tooltipLabelFormatter?: (label?: string) => string;
  showLegend?: boolean;
  legendProps?: Record<string, any>;
}

const DEFAULT_AREAS: AreaSpec[] = [
  {
    dataKey: "stockIn",
    stroke: "#16A34A",
    strokeWidth: 2,
    fill: "url(#stockInFill)",
    dot: false,
    type: "monotone",
  },
  {
    dataKey: "stockOut",
    stroke: "#DC2626",
    strokeWidth: 2,
    fill: "url(#stockOutFill)",
    dot: false,
    type: "monotone",
  },
];

const AreaChartWrapper: React.FC<AreaChartWrapperProps> = ({
  data,
  xKey = "date",
  areas,
  containerClassName = "mt-4 h-44 w-full",
  hideXAxis = false,
  hideYAxis = false,
  xAxisProps,
  yAxisProps,
  tooltipFormatter,
  tooltipLabelFormatter,
  showLegend = false,
  legendProps,
}) => {
  const resolvedAreas = areas && areas.length ? areas : DEFAULT_AREAS;
  const usesDefaultAreas = resolvedAreas === DEFAULT_AREAS;

  return (
    <div className={containerClassName}>
      <ResponsiveContainer width="100%" height="100%">
        <ReAreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          {usesDefaultAreas && (
            <defs>
              <linearGradient id="stockInFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4ADE80" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#4ADE80" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="stockOutFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F87171" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#F87171" stopOpacity={0.05} />
              </linearGradient>
            </defs>
          )}
          <CartesianGrid stroke="#E5E7EB" vertical={false} />
          <XAxis dataKey={xKey} hide={hideXAxis} {...xAxisProps} />
          <YAxis hide={hideYAxis} {...yAxisProps} />
          <Tooltip
            contentStyle={{
              borderRadius: "8px",
              borderColor: "#E5E7EB",
              fontSize: "12px",
            }}
            labelStyle={{ fontWeight: 600 }}
            formatter={tooltipFormatter}
            labelFormatter={tooltipLabelFormatter}
          />
          {showLegend && <Legend {...legendProps} />}
          {resolvedAreas.map((area) => (
            <Area
              key={area.dataKey}
              type={area.type || "monotone"}
              dataKey={area.dataKey}
              stroke={area.stroke}
              strokeWidth={area.strokeWidth ?? 2}
              fill={area.fill || area.stroke}
              fillOpacity={area.fillOpacity}
              dot={area.dot ?? false}
            />
          ))}
        </ReAreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AreaChartWrapper;
